import React from 'react';
import Markdown from 'react-markdown';
import { Box, Typography } from '@mui/material';

interface SimpleMarkdownRendererProps {
  /**
   * Markdown content to render
   */
  markdown: string;
  
  /**
   * Optional className for styling wrapper
   */
  className?: string;
  
  /**
   * Optional sx prop for custom styling
   */
  sx?: object;
}

/**
 * Lightweight markdown renderer for read-only display.
 * Uses react-markdown instead of BlockNote for better memory efficiency.
 * 
 * Applies small font sizes matching the compliance rule card styling.
 */
export const SimpleMarkdownRenderer: React.FC<SimpleMarkdownRendererProps> = ({
  markdown,
  className,
  sx,
}) => {
  return (
    <Box
      className={className}
      sx={{
        color: 'textColors.darkText',
        padding: '0.25rem 0.5rem',
        ...sx,
      }}
    >
      <Markdown
        components={{
          p: ({ children }) => (
            <Typography 
              component="p" 
              variant="body2"
              sx={{ fontSize: '0.75rem', lineHeight: 1.3, margin: '0.25em 0' }}
            >
              {children}
            </Typography>
          ),
          h1: ({ children }) => (
            <Typography 
              component="h1" 
              variant="h6"
              sx={{ fontSize: '0.875rem', lineHeight: 1.2, margin: '0.5em 0 0.25em 0', fontWeight: 600 }}
            >
              {children}
            </Typography>
          ),
          h2: ({ children }) => (
            <Typography 
              component="h2" 
              variant="h6"
              sx={{ fontSize: '0.8125rem', lineHeight: 1.2, margin: '0.5em 0 0.25em 0', fontWeight: 600 }}
            >
              {children}
            </Typography>
          ),
          h3: ({ children }) => (
            <Typography 
              component="h3" 
              variant="subtitle1"
              sx={{ fontSize: '0.78125rem', lineHeight: 1.2, margin: '0.5em 0 0.25em 0', fontWeight: 600 }}
            >
              {children}
            </Typography>
          ),
          strong: ({ children }) => <strong style={{ fontSize: '0.75rem', fontWeight: 600 }}>{children}</strong>,
          em: ({ children }) => <em style={{ fontSize: '0.75rem', fontStyle: 'italic' }}>{children}</em>,
          // Links here point off-app (IRS, NPPES, CAQH, state boards). Without
          // an override react-markdown renders a same-tab anchor, which would
          // navigate a user out of whatever they were part-way through filling in.
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '0.75rem' }}
            >
              {children}
            </a>
          ),
          code: ({ children }) => (
            <code style={{ 
              fontSize: '0.75rem', 
              fontFamily: 'monospace', 
              backgroundColor: 'rgba(0, 0, 0, 0.05)', 
              padding: '0.125em 0.25em', 
              borderRadius: '0.25em' 
            }}>
              {children}
            </code>
          ),
          pre: ({ children }) => (
            <pre style={{ 
              fontSize: '0.75rem', 
              fontFamily: 'monospace', 
              backgroundColor: 'rgba(0, 0, 0, 0.05)', 
              padding: '0.5em', 
              borderRadius: '0.25em', 
              overflow: 'auto' 
            }}>
              {children}
            </pre>
          ),
          ul: ({ children }) => (
            <ul style={{ fontSize: '0.75rem', lineHeight: 1.3, margin: '0.25em 0', paddingLeft: '1.2em' }}>
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol style={{ fontSize: '0.75rem', lineHeight: 1.3, margin: '0.25em 0', paddingLeft: '1.2em' }}>
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li style={{ fontSize: '0.75rem', lineHeight: 1.3, margin: '0.125em 0' }}>
              {children}
            </li>
          ),
          blockquote: ({ children }) => (
            <blockquote style={{ 
              fontSize: '0.75rem', 
              borderLeft: '2px solid rgba(0, 0, 0, 0.12)', 
              paddingLeft: '0.5em', 
              margin: '0.25em 0', 
              fontStyle: 'italic' 
            }}>
              {children}
            </blockquote>
          ),
        }}
      >
        {markdown}
      </Markdown>
    </Box>
  );
};

